import { OcrComparisonResult } from '@fastre/core/schemas/ocr'
import { ArrowBackRounded, ArrowForwardRounded, DownloadRounded } from '@mui/icons-material'
import {
    Box,
    Button,
    CircularProgress,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Modal,
    ModalClose,
    ModalDialog,
    Sheet,
    Stack,
    Tooltip,
    Typography,
    useTheme,
} from '@mui/joy'
import { useAuth } from 'auth'
import { saveAs } from 'file-saver'
import { createRef, Suspense, useEffect, useMemo, useState } from 'react'
import { Document, Page, pdfjs } from 'react-pdf'
import 'react-pdf/dist/Page/AnnotationLayer.css'
import 'react-pdf/dist/Page/TextLayer.css'
import Input from './input'
import { dontCloseOnBackgroundClick } from './modal'

pdfjs.GlobalWorkerOptions.workerSrc = '/pdfjs-dist/build/pdf.worker.min.mjs'

interface Props {
    pdf?: string
    title: string
    onClose: () => void
    fileName: string
    ocrResult?: {
        [pageIndex: number]: OcrComparisonResult[]
    }
    hideDownloadButton?: boolean
    withAuth?: boolean
    isRawPdf?: boolean
}

const PdfModalComponent = ({
    pdf,
    title,
    onClose,
    fileName,
    ocrResult,
    hideDownloadButton,
    withAuth,
    isRawPdf,
}: Props) => {
    const theme = useTheme()
    const { token } = useAuth()

    const [numPages, setNumPages] = useState<number>()
    const [pageNumber, setPageNumber] = useState(1)
    const pageRef = createRef<HTMLDivElement>()

    const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
        setNumPages(numPages)
        setPageNumber(1)
    }

    const padding = 0.03

    const options = useMemo(
        () => ({
            cMapUrl: '/cmaps/',
            cMapPacked: true,
            ...(withAuth
                ? {
                      withCredentials: true,
                      httpHeaders: {
                          Authorization: `Bearer ${token}`,
                      },
                  }
                : {}),
        }),
        [token, withAuth],
    )

    const fileSource = useMemo(() => {
        if (!pdf) return undefined
        if (isRawPdf) {
            try {
                // This is base64-encoded PDF data from the Zoho API
                // Decode it back to binary data
                const binaryString = atob(pdf)
                const bytes = new Uint8Array(binaryString.length)
                for (let i = 0; i < binaryString.length; i++) {
                    bytes[i] = binaryString.charCodeAt(i)
                }
                return { data: bytes }
            } catch (error) {
                console.error('Error processing PDF data:', error)
                return undefined
            }
        }
        return pdf
    }, [pdf, isRawPdf])

    // Cleanup blob URL when component unmounts or pdf changes
    useEffect(() => {
        return () => {
            if (fileSource && isRawPdf && typeof fileSource === 'string' && fileSource.startsWith('blob:')) {
                URL.revokeObjectURL(fileSource)
            }
        }
    }, [fileSource, isRawPdf])

    return (
        <Modal
            open={pdf != undefined}
            onClose={dontCloseOnBackgroundClick(onClose)}
        >
            <ModalDialog
                sx={{
                    overflow: 'auto',
                }}
            >
                <ModalClose sx={{ zIndex: 3 }} />
                <DialogTitle>{title}</DialogTitle>
                <DialogContent
                    sx={{
                        width: '810px',
                    }}
                >
                    <Box sx={{ position: 'relative' }}>
                        {fileSource && (
                            <Box
                                sx={{
                                    height: '1010px',
                                    position: 'relative',
                                }}
                            >
                                <Document
                                    file={fileSource}
                                    onLoadSuccess={onDocumentLoadSuccess}
                                    onLoadError={error => {
                                        console.error('Error loading PDF:', error)
                                    }}
                                    options={options}
                                >
                                    <Box ref={pageRef}>
                                        <Page
                                            pageNumber={pageNumber}
                                            scale={1.33}
                                            //width={containerWidth ? Math.min(containerWidth, maxWidth) : maxWidth}
                                        />
                                    </Box>
                                </Document>
                                {ocrResult?.[pageNumber] &&
                                    ocrResult[pageNumber].map(
                                        ({ name, success, expectedValue, boundingBox }) => (
                                            <Tooltip
                                                key={name}
                                                title={expectedValue}
                                            >
                                                <Sheet
                                                    key={name}
                                                    color={success ? 'success' : 'danger'}
                                                    variant="soft"
                                                    sx={{
                                                        position: 'absolute',
                                                        top: boundingBox.top - padding + 'in',
                                                        height: boundingBox.height + padding * 2 + 'in',
                                                        left: boundingBox.left - padding + 'in',
                                                        width: boundingBox.width + padding * 2 + 'in',
                                                        zIndex: 3,
                                                        opacity: 0.3,
                                                        //margin: '-2px'
                                                    }}
                                                ></Sheet>
                                            </Tooltip>
                                        ),
                                    )}
                            </Box>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions sx={{ position: 'relative' }}>
                    <Stack
                        direction="row"
                        gap={2}
                        alignItems="center"
                        sx={{ margin: 'auto' }}
                    >
                        <IconButton
                            onClick={() => setPageNumber(pageNumber - 1)}
                            disabled={pageNumber <= 1}
                        >
                            <ArrowBackRounded />
                        </IconButton>
                        <Stack
                            direction="row"
                            gap={1}
                            alignItems="center"
                        >
                            <Typography sx={{ userSelect: 'none' }}>Page</Typography>
                            <Input
                                type="number"
                                value={pageNumber}
                                onChange={x => setPageNumber(Math.min(x as any, numPages ?? 1))}
                                sx={{ width: '50px' }}
                                onFocus={e => e.target.select()}
                            />
                            <Typography sx={{ userSelect: 'none' }}>of {numPages}</Typography>
                        </Stack>
                        <IconButton
                            onClick={() => setPageNumber(pageNumber + 1)}
                            disabled={pageNumber >= (numPages ?? 1)}
                        >
                            <ArrowForwardRounded />
                        </IconButton>
                    </Stack>
                    {!hideDownloadButton && (
                        <Button
                            variant="outlined"
                            sx={{
                                position: 'absolute',
                                right: 0,
                                [theme.breakpoints.down('sm')]: {
                                    display: 'none',
                                },
                            }}
                            onClick={() => {
                                if (isRawPdf && pdf) {
                                    try {
                                        // Decode base64 PDF data for download
                                        const binaryString = atob(pdf)
                                        const bytes = new Uint8Array(binaryString.length)
                                        for (let i = 0; i < binaryString.length; i++) {
                                            bytes[i] = binaryString.charCodeAt(i)
                                        }
                                        const blob = new Blob([bytes], { type: 'application/pdf' })
                                        saveAs(blob, fileName)
                                    } catch (error) {
                                        console.error('Error downloading PDF:', error)
                                        // Fallback to original string if conversion fails
                                        saveAs(pdf, fileName)
                                    }
                                } else {
                                    saveAs(pdf, fileName)
                                }
                            }}
                            startDecorator={<DownloadRounded />}
                        >
                            Download
                        </Button>
                    )}
                </DialogActions>
            </ModalDialog>
        </Modal>
    )
}

// Loading fallback component
const PdfModalLoader = () => (
    <Box
        sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '200px',
        }}
    >
        <CircularProgress />
    </Box>
)

// Lazy-loaded PdfModal component
const PdfModal = (props: Props) => (
    <Suspense fallback={<PdfModalLoader />}>
        <PdfModalComponent {...props} />
    </Suspense>
)

export default PdfModal
